import { inject, Injectable } from '@angular/core';
import { NETWORK_GROUP_LABEL_MAP, ProgramGroupService } from '@services/program-group.service';
import { UserService } from '@services/user.service';
import { FilterDef } from '@shared/dataview/core/dataview-types';
import { FilterOptionsContext, groupByValue, SourceFilterOptionsLoader } from '@shared/dataview/options/filter-options.registry';
import { SelectListItem } from '@shared/models/select-list-item';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProgramGroupsOptionsLoader extends SourceFilterOptionsLoader {
  readonly source = 'programGroups';

  private readonly programGroupService = inject(ProgramGroupService);

  load(def: FilterDef, context: FilterOptionsContext): Observable<SelectListItem[]> {
    const filterId = def.id === 'programGroupId' ? 'programGroupId' : 'pharmacyProgramGroup';

    return this.programGroupService.getAllProgramGroupsForCurrentUser().pipe(
      map(groupsByTenant =>
        this.programGroupService.filterAndSortBlockedProgramGroupsForDataview(groupsByTenant, filterId, UserService.getCurrentRole()),
      ),
      map(groupsByTenant => this.toOptions(groupsByTenant, context)),
    );
  }

  private toOptions(
    groupsByTenant: Record<string, Array<{ id: string; name: string; networkType: string }>>,
    context: FilterOptionsContext,
  ): SelectListItem[] {
    const options: SelectListItem[] = [];
    const tenantIds = Object.keys(groupsByTenant).sort((a, b) => a.localeCompare(b));

    for (const tenantId of tenantIds) {
      const tenantName = context.accessibleTenants.find(tenant => tenant.id === tenantId)?.name ?? 'Other';
      let lastNetworkType = '';

      for (const group of groupsByTenant[tenantId]) {
        const startsNewNetworkType = group.networkType !== lastNetworkType;
        lastNetworkType = group.networkType;

        options.push({
          name: group.name,
          value: groupByValue(tenantId, group.id),
          group: context.groupByTenant ? tenantName : undefined,
          subGroupTitle: startsNewNetworkType
            ? NETWORK_GROUP_LABEL_MAP[group.networkType as keyof typeof NETWORK_GROUP_LABEL_MAP]
            : undefined,
        });
      }
    }

    return options;
  }
}
